import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { MessageSquare, Send, Calendar, Megaphone, Vote, User as UserIcon, Smile, Image as ImageIcon, Mic, Contact as ContactIcon, X, Square, Loader2, AlertCircle } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import ImageLightbox from '../components/ImageLightbox';

interface Post {
  id: string;
  title: string;
  content: string;
  author: string;
  type: string;
  created_at: string;
  attachments?: Attachment[];
}

interface Attachment {
  type: 'image' | 'audio' | 'contact' | 'file';
  name: string;
  mimeType: string;
  size: number;
  data: string; // data URI (data:<mime>;base64,...) or plain text for vCard
}

// Cap a single attachment ~1.5MB of base64 (mirrors backend guard).
const MAX_ATTACHMENT_BASE64 = 1.5 * 1024 * 1024;

// Silent background refresh cadence for the wall feed (matches Mensajes thread list).
const WALL_POLL_MS = 7000;

// Parse the messages-table JSONB `attachments` column (array or JSON string).
const parseAttachments = (raw: any): Attachment[] => {
  let arr: any = raw;
  if (typeof raw === 'string') {
    try { arr = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((a: any) => a && typeof a === 'object' && typeof a.data === 'string' && a.data)
    .map((a: any) => ({
      type: (['image', 'audio', 'contact', 'file'].includes(a.type) ? a.type : 'file') as Attachment['type'],
      name: String(a.name || 'archivo'),
      mimeType: String(a.mimeType || ''),
      size: Number(a.size) || a.data.length,
      data: a.data,
    }));
};

// Common unicode emoji set — no external dependency.
const EMOJI_GROUPS: string[][] = [
  '😀 😄 😁 😆 😅 😂 🙂 😉 😊 😍 😘 😜 🤔 🤗 😴 😢 😭 😤 😡 😱'.split(' '),
  '👍 👎 👏 🙏 💪 🤝 ✌️ 👋 ❤️ 💔 ✨ 🎉 🎊 🔥 💯 ⚠️ ❓ ✅ ❌ ⭐'.split(' '),
  '🏠 🏢 🌳 🚗 🐕 🐈 🌮 ☕ 🍕 🎂 🎁 📦 🔧 🛠️ 💧 💡 📱 💳 🧹 🌧️'.split(' '),
];

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Muro — Community wall/feed.
 * Shows recent announcements, events, and votes in a social-feed layout.
 * Distinct from the Announcements admin page — this is the resident-facing wall.
 * Chat enhancements: emoji picker, image attachments, voice notes (MediaRecorder),
 * and contact sharing (vCard) — all stored in the messages.attachments JSONB column.
 *
 * Modernization 2026-08-29 (matches the redesigned Mensajes page): white/slate
 * surfaces with dark: variants, teal accents only (zero purple), text-base
 * textarea (no iOS focus zoom), rounded-2xl composer (textarea on top expanding
 * upward, button row below), and silent background polling of the feed — the
 * visible spinner stays only for initial load; poll errors keep the last-good
 * list. Post creation / API shape unchanged (POST /communication/condo/:id/messages).
 */
const WallPage: React.FC = () => {
  const { user, isAuthenticated, activeCondoId } = useAuth();
  const [systemPosts, setSystemPosts] = useState<Post[]>([]);
  const [userPosts, setUserPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // F108: Post composer state
  const [composerContent, setComposerContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');

  // Chat-enhancement state
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  // In-app image viewer (lightbox) — replaces target=_blank data: URI navigation
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const [recordError, setRecordError] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const postingRef = useRef(false);

  // Prefer the auth context's resolved active condo; fall back to user.condoId.
  const condoId = (isAuthenticated && activeCondoId) || user?.condoId;

  // Merged feed: system posts (anuncios/eventos/votaciones) + resident posts,
  // oldest (top) → newest (bottom) — chat-style order.
  const posts = React.useMemo(() => {
    const merged = [...systemPosts, ...userPosts];
    merged.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    return merged;
  }, [systemPosts, userPosts]);

  // Keep the newest message visible (chat behaviour): programmatic container
  // autoscroll to the bottom on load and after each send (not focus-scroll).
  useEffect(() => {
    const el = listContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }, [posts.length, loading]);

  // System posts (announcements / calendar events / polls) — initial load with
  // the empty-feed retry. Background feed polls only touch userPosts, so these
  // are never clobbered.
  useEffect(() => {
    if (!condoId) { setLoading(false); return; }
    let cancelled = false;
    const load = async (attempt: number) => {
      // Per-invocation flag: must NOT be shared across retries. Previously it lived
      // in the effect scope and was never reset, so once attempt 0 scheduled a retry,
      // the final attempt's `finally` saw willRetry===true and never ran
      // setLoading(false) — leaving the wall spinner spinning forever.
      let willRetry = false;
      try {
        const [annRes, calRes, voteRes] = await Promise.allSettled([
          api.get(`/announcements/condo/${condoId}`),
          api.get(`/calendar/condo/${condoId}/events`),
          api.get(`/voting/condo/${condoId}/polls`),
        ]);

        const allPosts: Post[] = [];

        if (annRes.status === 'fulfilled') {
          const items = annRes.value.data?.data ?? annRes.value.data ?? [];
          if (Array.isArray(items)) {
            items.forEach((a: any) => allPosts.push({
              id: a.id, title: a.title || 'Anuncio', content: a.content || a.message || '',
              author: a.author || a.created_by_name || 'Administración', type: 'anuncio',
              created_at: a.created_at || a.date || new Date().toISOString(),
            }));
          }
        }
        if (calRes.status === 'fulfilled') {
          const items = calRes.value.data?.data ?? calRes.value.data ?? [];
          if (Array.isArray(items)) {
            items.slice(0, 5).forEach((e: any) => allPosts.push({
              id: e.id, title: e.title || 'Evento', content: `${e.description || ''} ${e.location ? '— ' + e.location : ''}`.trim(),
              author: 'Calendario', type: 'evento', created_at: e.date || e.event_date || new Date().toISOString(),
            }));
          }
        }
        if (voteRes.status === 'fulfilled') {
          const items = voteRes.value.data?.data ?? voteRes.value.data ?? [];
          if (Array.isArray(items)) {
            items.slice(0, 3).forEach((v: any) => allPosts.push({
              id: v.id, title: v.title || v.question || 'Votación', content: v.description || '',
              author: 'Votaciones', type: 'votacion', created_at: v.created_at || new Date().toISOString(),
            }));
          }
        }

        allPosts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        if (cancelled) return;

        // Wall/condo data may not be ready yet (race with auth context);
        // retry up to 3 times with 500ms back-off if the feed came back empty.
        if (allPosts.length === 0 && attempt < 3) {
          willRetry = true;
          setTimeout(() => { if (!cancelled) load(attempt + 1); }, 500);
          return;
        }

        setSystemPosts(allPosts);
      } catch (e: any) {
        if (!cancelled) setError('No se pudo cargar el muro');
      } finally { if (!cancelled && !willRetry) setLoading(false); }
    };
    load(0);
    return () => { cancelled = true; };
  }, [condoId]);

  // F108: resident wall posts from the communication API. Initial (visible)
  // load plus a silent background poll every WALL_POLL_MS while the tab is
  // visible. Silent polls never show a spinner and, on error, keep the
  // last-good list. Silent refreshes no-op when nothing changed.
  const fetchUserPosts = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!condoId) return;
    if (!silent) { setLoading(true); setError(''); }
    try {
      const res = await api.get(`/communication/condo/${condoId}/messages`);
      const data = res.data?.data ?? res.data;
      const items = Array.isArray(data) ? data : data?.items ?? [];
      const next: Post[] = items.map((m: any) => ({
        id: m.id,
        title: m.sender_name || m.author || 'Vecino',
        content: m.content || '',
        author: m.sender_name || m.author || 'Vecino',
        type: 'mensaje',
        created_at: m.created_at || m.timestamp || new Date().toISOString(),
        attachments: parseAttachments(m.attachments),
      }));
      setUserPosts(prev => {
        if (silent && prev.length === next.length && prev.every((p, i) => p.id === next[i]?.id)) return prev;
        return next;
      });
    } catch {
      if (!silent) setError('No se pudo cargar el muro');
      // Silent failures keep the last good list; transient poll errors are ignored.
    } finally {
      if (!silent) setLoading(false);
    }
  }, [condoId]);

  useEffect(() => { fetchUserPosts(); }, [fetchUserPosts]);

  // Silent background feed polling (7s) — only while the tab is visible;
  // cleared on unmount / when visibility flips. Skipped while a post is in
  // flight so an in-flight send is never clobbered (same pattern as Mensajes).
  useEffect(() => {
    if (!condoId) return;
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (interval === null) interval = setInterval(() => {
        if (!postingRef.current) fetchUserPosts({ silent: true });
      }, WALL_POLL_MS);
    };
    const stop = () => { if (interval !== null) { clearInterval(interval); interval = null; } };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start(); else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [condoId, fetchUserPosts]);

  // ---------- Emoji picker ----------
  const insertAtCursor = useCallback((text: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setComposerContent(prev => prev + text);
      return;
    }
    const start = ta.selectionStart ?? composerContent.length;
    const end = ta.selectionEnd ?? start;
    const next = composerContent.slice(0, start) + text + composerContent.slice(end);
    setComposerContent(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  }, [composerContent]);

  // WhatsApp-style auto-expand: start at 1 line, grow up to ~5 lines (max 120px),
  // scrollable after that. The pill grows with the textarea.
  const autoResizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  // Reset the auto-expanded textarea back to one line (after send).
  const resetTextareaHeight = () => {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) el.style.height = 'auto';
    });
  };

  // The textarea rests at ONE line; it expands upward only while typing and
  // collapses back to one line on blur (even if multi-line text remains —
  // overflow-y scrolls it). Same behaviour as the Mensajes composer.
  const collapseTextareaOnBlur = () => {
    const el = textareaRef.current;
    if (el) el.style.height = '44px';
  };

  // ---------- Image attachments (base64 JSON — no FormData needed) ----------
  const addImageFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).slice(0, 4).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > MAX_ATTACHMENT_BASE64) {
        setPostError(`La imagen "${file.name}" supera el límite de 1.5 MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const data = String(reader.result || '');
        if (data.length > MAX_ATTACHMENT_BASE64) {
          setPostError(`La imagen "${file.name}" supera el límite de 1.5 MB`);
          return;
        }
        setPendingAttachments(prev => [...prev, {
          type: 'image', name: file.name, mimeType: file.type, size: data.length, data,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // ---------- Voice notes (MediaRecorder → base64) ----------
  const startRecording = async () => {
    setRecordError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setRecordError('Tu navegador no soporta notas de voz');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const reader = new FileReader();
        reader.onload = () => {
          const data = String(reader.result || '');
          if (data.length > MAX_ATTACHMENT_BASE64) {
            setRecordError('La nota de voz es demasiado larga (máx ~1.5 MB)');
            return;
          }
          setPendingAttachments(prev => [...prev, {
            type: 'audio',
            name: `nota-de-voz-${new Date().toISOString().slice(11, 19)}.webm`,
            mimeType: blob.type || 'audio/webm',
            size: data.length,
            data,
          }]);
        };
        reader.readAsDataURL(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setRecordError('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  // ---------- Contact sharing (vCard 3.0 attached as .vcf) ----------
  const shareMyContact = () => {
    const name = user?.name || 'Vecino';
    const anyUser: any = user || {};
    const email = anyUser.email || '';
    const phone = anyUser.phone || anyUser.phoneNumber || anyUser.telefono || '';
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${name}`,
      `N:${name.split(' ').slice(-1)[0] || name};${name.split(' ')[0] || ''};;;`,
      email ? `EMAIL:${email}` : '',
      phone ? `TEL;TYPE=CELL:${phone}` : '',
      'END:VCARD',
    ].filter(Boolean).join('\r\n');
    const data = `data:text/vcard;base64,${btoa(unescape(encodeURIComponent(vcard)))}`;
    setPendingAttachments(prev => prev.some(a => a.type === 'contact')
      ? prev
      : [...prev, { type: 'contact', name: `${name}.vcf`, mimeType: 'text/vcard', size: data.length, data }]);
  };

  const removePending = (idx: number) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  // ---------- Post ----------
  const handlePost = async () => {
    setPostError('');
    if (!composerContent.trim() && pendingAttachments.length === 0) {
      setPostError('Escribe algo para publicar');
      return;
    }
    if (!condoId) {
      setPostError('Sin condominio asignado');
      return;
    }

    setPosting(true);
    postingRef.current = true;
    try {
      const res = await api.post(`/communication/condo/${condoId}/messages`, {
        content: composerContent.trim(),
        attachments: pendingAttachments,
      });
      const data = res.data?.data ?? res.data;
      const newPost: Post = {
        id: data?.message?.id || data?.id || crypto.randomUUID(),
        title: data?.message?.sender_name || user?.name || 'Vecino',
        content: data?.message?.content || composerContent.trim(),
        author: data?.message?.sender_name || user?.name || 'Vecino',
        type: 'mensaje',
        created_at: data?.message?.created_at || new Date().toISOString(),
        attachments: pendingAttachments.length ? pendingAttachments : parseAttachments(data?.message?.attachments),
      };
      // Merge by id: if the background poll already delivered the saved post,
      // don't duplicate it in the feed.
      setUserPosts(prev => prev.some(p => p.id === newPost.id) ? prev : [...prev, newPost]);
      setComposerContent('');
      setPendingAttachments([]);
      setShowEmoji(false);
      resetTextareaHeight();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al publicar';
      setPostError(msg);
    } finally {
      setPosting(false);
      postingRef.current = false;
    }
  };

  const iconFor = (type: string) => {
    if (type === 'anuncio') return <Megaphone className="w-5 h-5 text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)]" />;
    if (type === 'evento') return <Calendar className="w-5 h-5 text-blue-500" />;
    if (type === 'votacion') return <Vote className="w-5 h-5 text-amber-500" />;
    if (type === 'mensaje') return <UserIcon className="w-5 h-5 text-white" />;
    return <MessageSquare className="w-5 h-5 text-gray-400" />;
  };

  // Renders one attachment inside a post (images, audio, vCard contacts, files).
  const renderAttachment = (a: Attachment, i: number) => {
    if (a.type === 'image') {
      return (
        <button
          type="button"
          key={i}
          onClick={() => setLightboxImage({ src: a.data, alt: a.name })}
          className="block cursor-zoom-in"
        >
          <img
            src={a.data}
            alt={a.name}
            className="max-h-56 max-w-full rounded-lg border border-black/10 dark:border-white/10 object-cover hover:opacity-90 transition-opacity"
            loading="lazy"
          />
        </button>
      );
    }
    if (a.type === 'audio') {
      return (
        <audio key={i} controls src={a.data} className="w-full max-w-[240px] min-w-0 h-10" />
      );
    }
    if (a.type === 'contact') {
      return (
        <a
          key={i}
          href={a.data}
          download={a.name}
          className="flex items-center gap-3 bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-[var(--v-surface-3)] rounded-lg p-3 w-full max-w-[240px] min-w-0 hover:border-[var(--v-teal-400)] transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-[var(--v-teal-400)]/10 flex items-center justify-center flex-shrink-0">
            <ContactIcon className="w-5 h-5 text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{a.name.replace(/\.vcf$/i, '')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Tarjeta de contacto — toca para guardar</p>
          </div>
        </a>
      );
    }
    return (
      <a
        key={i}
        href={a.data}
        download={a.name}
        className="flex items-center gap-2 text-sm text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)] underline break-all"
      >
        📎 {a.name} ({formatBytes(a.size)})
      </a>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-400)]" />
    </div>
  );

  if (!condoId) return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
      <p className="text-gray-500 dark:text-gray-400">Sin condominio asignado</p>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-2xl mx-auto overflow-x-hidden bg-gray-50 dark:bg-[var(--v-base-bg)]">
      {/* Header — same design language as the Mensajes header */}
      <div className="flex-shrink-0 bg-white dark:bg-[var(--v-surface-1)] border-b border-gray-100 dark:border-[var(--v-surface-3)] px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 bg-gradient-to-br from-[var(--v-teal-500)] to-[var(--v-teal-700)] rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
          <MessageSquare className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-[var(--v-text-primary)] truncate">Muro de la Comunidad</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Anuncios, eventos y vecinos</p>
        </div>
      </div>
      {error && (
        <div className="mx-4 mt-2 flex-shrink-0 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Chat-style message list: oldest (top) → newest (bottom); scrolls, composer stays pinned */}
      <div ref={listContainerRef} className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-4 py-3 pb-2 space-y-3">
        {posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <MessageSquare className="w-12 h-12 text-gray-200 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-lg font-bold">No hay publicaciones aún</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Los anuncios y eventos del condominio aparecerán aquí.</p>
          </div>
        ) : (
          posts.map(post => {
            const isMensaje = post.type === 'mensaje';
            return (
              <div key={post.id} className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 dark:border-[var(--v-surface-3)] p-4 shadow-sm">
                <div className="flex items-start gap-3 min-w-0">
                  {isMensaje ? (
                    <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-[var(--v-teal-600)] flex items-center justify-center">
                      {iconFor(post.type)}
                    </div>
                  ) : (
                    <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
                      {iconFor(post.type)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900 dark:text-white break-words">{post.title}</h3>
                      {post.type === 'anuncio' && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-[var(--v-teal-400)]/10 dark:bg-[var(--v-teal-500)]/20 text-[var(--v-teal-700)] dark:text-[var(--v-teal-300)]">Anuncio</span>
                      )}
                      {post.type === 'evento' && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300">Evento</span>
                      )}
                      {post.type === 'votacion' && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">Votación</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">{post.author} · {formatDateTime(post.created_at)}</p>
                    {post.content && <p className="text-gray-700 dark:text-gray-200 text-sm whitespace-pre-wrap break-words">{post.content}</p>}
                    {post.attachments && post.attachments.length > 0 && (
                      <div className="mt-2 flex flex-col gap-2">
                        {post.attachments.map((a, i) => renderAttachment(a, i))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer — same design as Mensajes: textarea on TOP (auto-expands upward),
          button row BELOW: emoji / imagen / voz / contacto left, counter + send right. */}
      <div className="flex-shrink-0 min-w-0 border-t border-gray-100 dark:border-[var(--v-surface-3)] bg-white dark:bg-[var(--v-surface-1)] px-4 py-3 relative">
        {/* Pending attachments */}
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {pendingAttachments.map((a, i) => (
              <div key={i} className="relative group">
                {a.type === 'image' ? (
                  <img src={a.data} alt={a.name} className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-slate-600" />
                ) : (
                  <div className="w-16 h-16 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 flex flex-col items-center justify-center p-1 text-center">
                    <span className="text-lg leading-none">{a.type === 'audio' ? '🎤' : '👤'}</span>
                    <span className="text-[9px] text-gray-400 truncate w-full">{a.type === 'audio' ? 'Nota de voz' : a.name}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePending(i)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
                  aria-label="Quitar adjunto"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Emoji picker popover */}
        {showEmoji && (
          <div className="absolute bottom-full left-4 mb-2 z-20 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-600 rounded-xl shadow-lg p-3 w-72 max-w-[calc(100vw-2rem)]">
            {EMOJI_GROUPS.map((group, gi) => (
              <div key={gi} className={gi > 0 ? 'mt-2' : ''}>
                <div className="grid grid-cols-10 gap-1">
                  {group.map((emoji, ei) => (
                    <button
                      key={ei}
                      type="button"
                      onClick={() => insertAtCursor(emoji)}
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-lg"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 min-w-0">
          <textarea
            ref={textareaRef}
            value={composerContent}
            onChange={(e) => setComposerContent(e.target.value)}
            onInput={autoResizeTextarea}
            onBlur={collapseTextareaOnBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!posting && (composerContent.trim() || pendingAttachments.length)) handlePost();
              }
            }}
            placeholder="Escribe un mensaje para tu comunidad..."
            rows={1}
            disabled={posting}
            maxLength={1000}
            className="w-full px-3 py-2.5 text-base border border-gray-200 dark:border-slate-600 rounded-2xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] resize-none text-left overflow-y-auto"
            style={{ width: '100%', textAlign: 'left', minHeight: '44px', maxHeight: '120px', lineHeight: '1.5' }}
          />

          <div className="flex items-center gap-1 min-w-0">
            {/* LEFT: emoji / imagen / nota de voz / contacto */}
            <button
              type="button"
              onClick={() => setShowEmoji(v => !v)}
              title="Emojis"
              className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0 ${showEmoji ? 'bg-gray-100 dark:bg-slate-700' : ''}`}
            >
              <Smile className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              title="Adjuntar imagen"
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
            >
              <ImageIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <button
              type="button"
              onClick={recording ? stopRecording : startRecording}
              title={recording ? 'Detener grabación' : 'Nota de voz'}
              className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${recording ? 'bg-red-100 dark:bg-red-900/40 animate-pulse' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}
            >
              {recording
                ? <Square className="w-4 h-4 text-red-500" />
                : <Mic className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
            </button>
            <button
              type="button"
              onClick={shareMyContact}
              title="Compartir mi contacto (vCard)"
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
            >
              <ContactIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { addImageFiles(e.target.files); e.target.value = ''; }}
            />

            {/* RIGHT: counter + Publicar (round teal send, same as chat) */}
            <span className="text-xs text-gray-400 ml-auto mr-2 flex-shrink-0">{composerContent.length}/1000</span>
            <button
              type="button"
              onClick={handlePost}
              disabled={posting || (!composerContent.trim() && pendingAttachments.length === 0)}
              aria-label="Publicar"
              title="Publicar"
              className="p-2.5 bg-[var(--v-teal-400)] text-white rounded-full hover:bg-[var(--v-teal-500)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
            >
              {posting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {recording && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" /> Grabando… toca ⏹ para terminar
          </p>
        )}
        {recordError && <p className="text-xs text-red-500 dark:text-red-400 mt-1">{recordError}</p>}
        {postError && (
          <div className="mt-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300">{postError}</p>
          </div>
        )}
      </div>
      {lightboxImage && (
        <ImageLightbox src={lightboxImage.src} alt={lightboxImage.alt} onClose={() => setLightboxImage(null)} />
      )}
    </div>
  );
};

export default WallPage;
